import { Injectable } from "@angular/core";
import { LocationStateStore, BulletinWidgetSettings } from "./location-state.store";
import { combineLatest, map, Observable } from "rxjs";
import { select } from "@ngneat/elf";

@Injectable({ providedIn: "root" })
export class LocationStateQuery {
	constructor(private locationStateStore: LocationStateStore) {}

	private hasBulletinWidgetContent(settings: BulletinWidgetSettings | null | undefined): boolean {
		if (settings == null) {
			return false;
		}

		const title = (settings.title ?? "").trim();
		const contentText = (settings.content ?? "")
			.replace(/<[^>]*>/g, " ")
			.replace(/&nbsp;/gi, " ")
			.replace(/\s+/g, " ")
			.trim();

		return title.length > 0 || contentText.length > 0;
	}

	getSnapshot = () => {
		return this.locationStateStore.store.getValue();
	};

	selectState$: Observable<"Open" | "Closed" | "Down"> = this.locationStateStore.store.pipe(
		select((state) => {
			return state.downtime != null ? "Down" : state.state;
		}),
	);

	preopeningChecklist$ = this.locationStateStore.store.pipe(select((state) => state.preopeningChecklist));
	closingChecklist$ = this.locationStateStore.store.pipe(select((state) => state.closingChecklist));
	createdDowntimesAreBlocking$ = this.locationStateStore.store.pipe(
		select((state) => state.createdDowntimesAreBlocking),
	);

	selectOperator$ = this.locationStateStore.store.pipe(select((state) => state.operator));
	operatorDateTimeSettings$ = this.selectOperator$.pipe(map((operator) => operator.dateTimeSettings));
	organizationDateTimeSettings$ = this.locationStateStore.store.pipe(
		select((state) => state.organizationDateTimeSettings),
	);

	locationIsOpen$ = this.locationStateStore.store.pipe(select((state) => state.state == "Open"));
	rideOpsKey$ = this.locationStateStore.store.pipe(select((state) => state.apiKey));
	rideOpsKeyAndOperator$ = this.locationStateStore.store.pipe(
		select((state) => [state.operator, state.apiKey] as const),
		map(([operator, key]) => [operator, key] as unknown as [string, number]),
	);

	location$ = this.locationStateStore.store.pipe(select((state) => state.location));
	locationProperties$ = this.locationStateStore.store.pipe(select((state) => state.locationProperties));
	locationName$ = this.locationStateStore.store.pipe(select((state) => state.location?.name));
	barcodeSignIn$ = this.locationStateStore.store.pipe(select((state) => state.location?.barcodeSignIn ?? false));
	isClearedForOperation$ = this.locationStateStore.store.pipe(
		select((state) => state.isClearedForOperationDetails.isClearedForOperation),
	);
	isClearedForPreopening$ = this.locationStateStore.store.pipe(
		select((state) => state.isClearedForOperationDetails.isClearedForPreopening),
	);
	isClearedForOperationDetails$ = this.locationStateStore.store.pipe(
		select((state) => state.isClearedForOperationDetails),
	);
	messageForOperatorWhenNotReadyForOperation$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.messageForOperatorWhenNotReadyForOperation),
	);
	messageForOperatorWhenNoCriticalForOperationChecklistsPlanned$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.messageForOperatorWhenNoCriticalForOperationChecklistsPlanned),
	);

	mandatoryPositionsWithoutAttendant$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.positions),
		map((positions) => positions?.filter((position) => position.mandatory && position.attendant == null) || []),
	);

	isSafeToOperate$ = combineLatest([
		this.selectState$.pipe(map((state) => state === "Down")),
		this.isClearedForOperation$,
		this.mandatoryPositionsWithoutAttendant$,
	]).pipe(
		map(
			([isDown, isClearedForOperation, mandatoryPositionsWithoutAttendant]) =>
				isDown === false && isClearedForOperation === true && mandatoryPositionsWithoutAttendant.length === 0,
		),
	);

	operatorIsSelected$ = this.locationStateStore.store.pipe(select((state) => !!state.operator));
	rideOpsKeyIsSet$ = this.locationStateStore.store.pipe(select((state) => !!state.apiKey));
	locationIsSet$ = this.locationStateStore.store.pipe(select((state) => !!state.location));
	canLogout$ = this.locationStateStore.store.pipe(select((state) => !!state.operator && state.state != "Open"));
	operators$ = this.locationStateStore.store.pipe(select((state) => state.operators));
	downtime$ = this.locationStateStore.store.pipe(select((state) => state.downtime));

	requiredCompetencies$ = this.locationStateStore.store.pipe(select((state) => state.requiredCompetencies));
	requiredCompetenciesForAttendants$ = this.locationStateStore.store.pipe(
		select((state) => state.requiredCompetenciesForAttendants),
	);

	noteTypes$ = this.locationStateStore.store.pipe(select((state) => state.noteTypes));
	noteTemplates$ = this.locationStateStore.store.pipe(select((state) => state.noteTemplates));
	downtimeTemplates$ = this.locationStateStore.store.pipe(select((state) => state.downtimeTemplates));

	lastQueueTimeUpdate$ = this.locationStateStore.store.pipe(select((state) => state.lastQueueUpdate));
	lastRidersUpdate$ = this.locationStateStore.store.pipe(select((state) => state.lastRidersUpdate));

	lastExternalDispatch$ = this.locationStateStore.store.pipe(select((state) => state.lastExternalDispatch));

	lastExternalQueueTime$ = this.locationStateStore.store.pipe(select((state) => state.lastExternalQueueTime));

	lastServerUpdate$ = this.locationStateStore.store.pipe(select((state) => state.lastServerUpdate));
	lastUpdateFailed$ = this.locationStateStore.store.pipe(select((state) => state.lastUpdateFailed));

	riders$ = this.locationStateStore.store.pipe(select((state) => state.riders));
	dispatches$ = this.locationStateStore.store.pipe(select((state) => state.dispatches));

	assetDispatchQueueIndex$ = this.locationStateStore.store.pipe(
		select((state) => state.assetDispatchQueueIndex ?? 0),
	);

	lastAssetDispatchCountByUnitId$ = this.locationStateStore.store.pipe(
		select((state) => state.lastAssetDispatchCountByUnitId ?? {}),
	);

	lastAssetDispatchOccupiedSeats$ = this.locationStateStore.store.pipe(
		select((state) => state.lastAssetDispatchOccupiedSeats),
	);

	locationOpenSessionStart$ = this.locationStateStore.store.pipe(select((state) => state.locationOpenSessionStart));

	dispatchCooldownUntil$ = this.locationStateStore.store.pipe(select((state) => state.dispatchCooldownUntil));

	operator$ = this.locationStateStore.store.pipe(select((state) => state.operator));
	attendants$ = this.locationStateStore.store.pipe(select((state) => state.rideOpsSettings.attendants));

	positions$ = this.locationStateStore.store.pipe(select((state) => state.rideOpsSettings.positions || []));
	allowStatisticsForOperator$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.allowStatisticsForOperator),
	);
	rideOpsSettings$ = this.locationStateStore.store.pipe(select((state) => state.rideOpsSettings));

	useAssetBasedDispatchConfiguration$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.useAssetBasedDispatchConfiguration ?? false),
	);

	assetUnits$ = this.locationStateStore.store.pipe(select((state) => state.rideOpsSettings.units ?? []));

	assetUnitOrderOverride$ = combineLatest([
		this.location$,
		this.locationStateStore.store.pipe(select((state) => state.assetUnitOrderByLocationId ?? {})),
	]).pipe(
		map(([location, orderByLocationId]) => {
			const locationId = location?.id;
			if (!locationId) {
				return null;
			}
			return orderByLocationId[locationId] ?? null;
		}),
	);

	dispatchUpdateWarningThreshold$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.dispatchUpdateWarningThreshold),
	);

	minumumSecondsBetweenDispatches$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.minumumSecondsBetweenDispatches),
	);

	externalDataSettings$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.externalDataSettings),
	);

	bulletinWidgetSettings$ = this.locationStateStore.store.pipe(
		select((state) => state.rideOpsSettings.bulletinWidgetSettings),
	);

	bulletinHasContent$ = this.bulletinWidgetSettings$.pipe(
		map((settings) => this.hasBulletinWidgetContent(settings)),
	);

	delayedDispatches$ = this.locationStateStore.store.pipe(select((state) => state.rideOpsSettings.delayedDispatches));

	emptyDispatches$ = this.locationStateStore.store.pipe(select((state) => state.rideOpsSettings.emptyDispatches));

	openingTime$ = this.locationStateStore.store.pipe(select((state) => state.openingTime));

	closingTime$ = this.locationStateStore.store.pipe(select((state) => state.closingTime));

	stateLastUpdated$ = this.locationStateStore.store.pipe(select((state) => state.stateLastUpdated));
}
